import React, { useEffect, useState, useRef } from 'react';
import api from '../api';
import { Link } from 'react-router-dom';
import useLang from '../hooks/useLang';

const importanceColors = {
  info:   '#3F72AF',
  low:    '#4c7a52',
  medium: '#e07b1a',
  high:   '#c0392b'
};

const importanceLabels = {
  info:   'Info',
  low:    'Low',
  medium: 'Medium',
  high:   'Urgent'
};

const importanceIcons = {
  info:   'ℹ️',
  low:    '🟢',
  medium: '🟠',
  high:   '🔴'
};

export default function TravelAlertsSection() {
  const [alerts, setAlerts]   = useState([]);
  const [index, setIndex]     = useState(0);
  const [visible, setVisible] = useState(true);
  const [loaded, setLoaded]   = useState(false);
  const timerRef              = useRef(null);
  const fadeRef               = useRef(null);
  var { pick } = useLang();

  useEffect(function() {
    api.get('/alerts?type=travel-alert')
      .then(function(res) {
        var sorted = res.data
          .sort(function(a, b) { return new Date(b.createdAt) - new Date(a.createdAt); })
          .slice(0, 3);
        setAlerts(sorted);
      })
      .catch(function() {})
      .finally(function() { setLoaded(true); });
  }, []);

  function goTo(i) {
    clearInterval(timerRef.current);
    clearTimeout(fadeRef.current);
    setVisible(false);
    fadeRef.current = setTimeout(function() {
      setIndex(i);
      setVisible(true);
    }, 450);
  }

  useEffect(function() {
    if (alerts.length < 2) return;
    timerRef.current = setInterval(function() {
      setVisible(false);
      fadeRef.current = setTimeout(function() {
        setIndex(function(i) { return (i + 1) % alerts.length; });
        setVisible(true);
      }, 450);
    }, 4000);
    return function() {
      clearInterval(timerRef.current);
      clearTimeout(fadeRef.current);
    };
  }, [alerts]);

  if (!loaded || alerts.length === 0) return null;

  var alert = alerts[index];
  var color = importanceColors[alert.importance] || importanceColors.info;

  return (
    <section id="travel-alerts-section" style={{ background: 'var(--bj-espresso)', padding: '72px 0' }}>
      <div className="container">
      <div className="two-col" style={{ gap: 40, alignItems: 'center' }}>

          {/* LEFT — static info panel */}
          <div>
            <span className="eyebrow" style={{ color: 'var(--bj-sand)' }}>Stay informed</span>
            <h2 style={{ color: '#fff', margin: '10px 0 16px', fontSize: '2rem' }}>
              Travel Alerts
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.65)', lineHeight: 1.75, marginBottom: 28 }}>
              Our team monitors visa requirements, travel advisories, and entry rules across
              all destinations we operate in. Check here before you travel.
            </p>

            {/* Importance legend */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {Object.keys(importanceColors).map(function(key) {
                return (
                  <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{
                      width: 10, height: 10, borderRadius: '50%',
                      background: importanceColors[key], flexShrink: 0
                    }} />
                    <span style={{
                      fontSize: '0.82rem', fontWeight: 700,
                      color: importanceColors[key],
                      textTransform: 'uppercase', letterSpacing: '0.06em',
                      minWidth: 60
                    }}>
                      {importanceLabels[key]}
                    </span>
                    <span style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.45)' }}>
                      {key === 'info'   && 'General travel information'}
                      {key === 'low'    && 'Minor update, good to know'}
                      {key === 'medium' && 'Notable change, plan ahead'}
                      {key === 'high'   && 'Urgent — act before travelling'}
                    </span>
                  </div>
                );
              })}
            </div>

            <Link
              to="/travel-alerts"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                marginTop: 32, color: 'var(--bj-sand)',
                fontWeight: 700, fontSize: '0.88rem',
                textDecoration: 'none'
              }}
            >
              View all alerts →
            </Link>
          </div>

          {/* RIGHT — fixed-height slider card */}
          <div>
            <div style={{
              position: 'relative',
              borderRadius: 14,
              background: '#fff',
              boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
              overflow: 'hidden',
              /* Fixed height prevents card resize on transition */
              height: 240,
              display: 'flex',
              flexDirection: 'column'
            }}>
              {/* Left color strip */}
              <div style={{
                position: 'absolute',
                left: 0, top: 0, bottom: 0,
                width: 6,
                background: color,
                opacity: visible ? 1 : 0,
                transition: 'opacity 0.45s ease, background 0.45s ease'
              }} />

              {/* Alert content — absolutely positioned so it never pushes height */}
              <div style={{
                position: 'absolute',
                inset: 0,
                padding: '26px 28px 20px 26px',
                display: 'flex',
                flexDirection: 'column',
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(10px)',
                transition: 'opacity 0.45s ease, transform 0.45s ease'
              }}>
                {/* Badge row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                  <span style={{ fontSize: '1.1rem' }}>
                    {importanceIcons[alert.importance] || 'ℹ️'}
                  </span>
                  <span style={{
                    background: color + '1a',
                    color: color,
                    fontSize: '0.68rem', fontWeight: 700,
                    letterSpacing: '0.1em', textTransform: 'uppercase',
                    padding: '3px 10px', borderRadius: 20
                  }}>
                    {importanceLabels[alert.importance] || 'Info'}
                  </span>
                  <span style={{ fontSize: '0.76rem', opacity: 0.4, marginLeft: 'auto' }}>
                    {new Date(alert.createdAt).toLocaleDateString('en-GB', {
                      day: '2-digit', month: 'short', year: 'numeric'
                    })}
                  </span>
                </div>

                {/* Title */}
                <h4 style={{ margin: '0 0 8px', fontSize: '1.05rem', color: 'var(--bj-espresso)', lineHeight: 1.3 }}>
                  {pick(alert, 'title')}
                </h4>

                {/* Message — clamp to avoid overflow changing layout */}
                <p style={{ margin: 0, fontSize: '0.9rem', opacity: 0.72, lineHeight: 1.7, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>
                  {pick(alert, 'message')}
                </p>
              </div>

              {/* Dots pinned to bottom */}
              {alerts.length > 1 && (
                <div style={{
                  position: 'absolute',
                  bottom: 14, left: 0, right: 0,
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 8
                }}>
                  {alerts.map(function(a, i) {
                    var dotColor = importanceColors[a.importance] || importanceColors.info;
                    return (
                      <button
                        key={a._id}
                        onClick={function() { goTo(i); }}
                        aria-label={'Show alert ' + (i + 1)}
                        style={{
                          width: i === index ? 24 : 8,
                          height: 8, borderRadius: 4,
                          border: 'none', padding: 0, cursor: 'pointer',
                          background: i === index ? dotColor : dotColor + '44',
                          transition: 'width 0.35s ease, background 0.35s ease'
                        }}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}